/**
 * sieve.ts — de zeefmassa's van een boring en wat je eruit kunt afleiden.
 *
 * Bij asbestonderzoek gaat de grond die uit het gat komt door een zeef van 20 mm. Wat er
 * doorheen valt en wat er op blijft liggen worden apart gewogen; samen zijn dat de twee
 * getallen die de veldwerker inspreekt. De totale massa wordt in de praktijk ook los gewogen
 * en wijkt dan iets af van de som -- er blijft altijd wat achter op de zeef en aan de handen.
 *
 * Daarom is het totaal INVULBAAR maar niet verplicht: staat er niets, dan is de som het
 * antwoord; typt de gebruiker een eigen weging, dan wint die. De percentages worden altijd
 * gerekend over het totaal dat op dat moment geldt, want anders tellen ze niet op tot 100 bij
 * een som en wél bij een losse weging -- of andersom, wat verwarrender is.
 *
 * Percentages worden NIET opgeslagen. Ze zijn een functie van drie getallen die er al staan;
 * bewaren zou betekenen dat ze kunnen gaan afwijken van de massa's waar ze bij horen.
 *
 * Gedeeld door backoffice en veldapp, zodat er niet twee rekenregels ontstaan die op de
 * tweede decimaal uit elkaar lopen.
 */

export type SieveMasses = {
  /** Totale massa van het monster (kg), zoals gewogen. Leeg = afleiden uit de fracties. */
  massTotalKg?: number | null;
  /** Massa van de fractie die NIET door de 20 mm-zeef valt (kg). */
  massGt20Kg?: number | null;
  /** Massa van de fractie die WEL door de 20 mm-zeef valt (kg). */
  massLt20Kg?: number | null;
};

export type SieveFractions = {
  /** Som van de twee fracties, of null als er geen enkele fractie is ingevuld. */
  computedTotalKg: number | null;
  /** Het totaal waarmee gerekend wordt: de ingevulde weging, anders de som. */
  effectiveTotalKg: number | null;
  /** True als de gebruiker een eigen totaal invulde dat van de som afwijkt. */
  overridden: boolean;
  /** Verschil tussen ingevuld totaal en som (kg), of null als er niets te vergelijken valt. */
  differenceKg: number | null;
  /** Aandeel > 20 mm in procenten, of null als het niet te bepalen is. */
  gt20Pct: number | null;
  /** Aandeel < 20 mm in procenten, of null als het niet te bepalen is. */
  lt20Pct: number | null;
};

/** Lege string, null en niet-getallen worden null; anders het getal. */
function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** Afronden op twee decimalen zonder drijvende-kommaruis (0.1 + 0.2 = 0.30000000000000004). */
const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Leid totaal en percentages af uit de ingevoerde massa's.
 *
 * Nooit een exceptie: een half ingevuld formulier is de normale toestand tijdens typen, en
 * een deling door nul hoort een leeg veld op te leveren en geen NaN in beeld.
 */
export function massFractions(masses: SieveMasses): SieveFractions {
  const total = num(masses.massTotalKg);
  const gt20 = num(masses.massGt20Kg);
  const lt20 = num(masses.massLt20Kg);

  const computedTotalKg = gt20 == null && lt20 == null ? null : round2((gt20 ?? 0) + (lt20 ?? 0));
  const effectiveTotalKg = total ?? computedTotalKg;

  const differenceKg = total != null && computedTotalKg != null ? round2(total - computedTotalKg) : null;
  const overridden = differenceKg != null && differenceKg !== 0;

  const pct = (v: number | null) =>
    v == null || effectiveTotalKg == null || effectiveTotalKg <= 0 ? null : round2((v / effectiveTotalKg) * 100);

  return {
    computedTotalKg,
    effectiveTotalKg,
    overridden,
    differenceKg,
    gt20Pct: pct(gt20),
    lt20Pct: pct(lt20),
  };
}

/** Nederlands getal: komma als decimaalteken, geen duizendtallen (zoals TerraIndex-remarks). */
const nl = (n: number) => String(Math.round(n * 100) / 100).replace('.', ',');

/**
 * De zeefanalyse als één regel tekst.
 *
 * TerraIndex heeft geen veld voor deze massa's -- nagelopen in de formulierdefinities van
 * meetpunt, laag, veldmonster, peilbuis en afwerking, en in elf echte exports. De enige
 * massa's die het kent zijn laboratoriumuitslagen op een analysemonster (AsComponentCode
 * 90040, in grammen), en een veldweging daar neerzetten zou haar als labuitslag presenteren.
 *
 * Dus gaat het als tekst mee in de opmerking van het meetpunt (MpRemark, vrij tekstveld van
 * 32767 tekens). Dat is precies waar TerraIndex-gebruikers dit soort losse veldmaten al
 * kwijtraken -- in echte exports staat daar "breedte ballastbed 4,90m" en "potten totaal 2".
 * Je kunt er in TerraIndex niet op rekenen of filteren, maar het gaat niet verloren.
 *
 * @returns de regel, of null als er niets gewogen is
 */
export function sieveSummaryText(masses: SieveMasses): string | null {
  const f = massFractions(masses);
  if (f.effectiveTotalKg == null) return null;

  const delen: string[] = [`totaal ${nl(f.effectiveTotalKg)} kg`];
  const deel = (kg: number | null | undefined, pct: number | null, label: string) => {
    if (kg == null) return;
    delen.push(`${label} ${nl(Number(kg))} kg${pct != null ? ` (${nl(pct)}%)` : ''}`);
  };
  deel(masses.massGt20Kg, f.gt20Pct, '>20 mm');
  deel(masses.massLt20Kg, f.lt20Pct, '<20 mm');

  // Wijkt het ingevulde totaal af van de som, dan hoort dat erbij: anders lijkt het in
  // TerraIndex alsof iemand zich verrekend heeft, terwijl er materiaal op de zeef achterbleef.
  const staart = f.overridden ? `; som van de fracties ${nl(f.computedTotalKg!)} kg` : '';
  return `Zeefanalyse 20 mm: ${delen.join(', ')}${staart}.`;
}
